import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { CheckCircle2, XCircle, ArrowRight, RotatCcw, Star, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { trackEvent } from '../utils/analytics';
import EXERCISES_A1 from '../data/exerciseBank';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── GAP FILL EXERCISE ───
function GapFill({ exercise, onComplete }) {
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState(false);

  const handleSelect = (idx, optIdx) => {
    if (checked) return;
    setAnswers(prev => ({ ...prev, [idx]: optIdx }));
  };

  const check = () => {
    setChecked(true);
    let correct = 0;
    exercise.sentences.forEach((s, i) => { if (answers[i] === s.answer) correct++; });
    onComplete(correct, exercise.sentences.length);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground italic mb-4">{exercise.instruction}</p>
      {exercise.sentences.map((s, i) => (
        <div key={`gap-${i}`} className="p-3 rounded-lg bg-muted/30 border border-border">
          <p className="text-sm mb-2 font-medium">{s.text}</p>
          <div className="flex flex-wrap gap-2">
            {s.options.map((opt, oi) => {
              let cls = 'px-3 py-1.5 text-xs rounded-md border cursor-pointer transition-all ';
              if (checked) {
                if (oi === s.answer) cls += 'border-emerald-500 bg-emerald-500/15 text-emerald-400';
                else if (answers[i] === oi) cls += 'border-red-500 bg-red-500/10 text-red-400';
                else cls += 'border-border opacity-40';
              } else {
                cls += answers[i] === oi ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50';
              }
              return <button key={`${i}-${oi}`} className={cls} onClick={() => handleSelect(i, oi)}>{opt}</button>;
            })}
          </div>
        </div>
      ))}
      {!checked && (
        <Button size="sm" onClick={check} disabled={Object.keys(answers).length < exercise.sentences.length} className="w-full bg-teal-600 hover:bg-teal-700">
          Check / Prüfen
        </Button>
      )}
    </div>
  );
}

// ─── WORD SORT EXERCISE ───
function WordSort({ exercise, onComplete }) {
  const [placements, setPlacements] = useState({});
  const [checked, setChecked] = useState(false);
  const [selectedWord, setSelectedWord] = useState(null);

  const placeWord = (wordIdx, catIdx) => {
    if (checked) return;
    setPlacements(prev => ({ ...prev, [wordIdx]: catIdx }));
    setSelectedWord(null);
  };

  const undoWord = (wordIdx) => {
    if (checked) return;
    setPlacements(prev => { const n = { ...prev }; delete n[wordIdx]; return n; });
  };

  const check = () => {
    setChecked(true);
    let correct = 0;
    exercise.items.forEach((item, i) => { if (placements[i] === item.cat) correct++; });
    onComplete(correct, exercise.items.length);
  };

  const catColors = ['rgba(59,130,246,0.15)', 'rgba(245,158,11,0.15)', 'rgba(168,85,247,0.15)'];
  const catBorders = ['rgba(59,130,246,0.5)', 'rgba(245,158,11,0.5)', 'rgba(168,85,247,0.5)'];
  const catText = ['text-blue-400', 'text-amber-400', 'text-purple-400'];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground italic mb-2">{exercise.instruction}</p>

      {/* Category drop zones */}
      <div className="grid grid-cols-2 gap-3" style={exercise.categories.length === 3 ? {gridTemplateColumns: 'repeat(3, 1fr)'} : {}}>
        {exercise.categories.map((cat, ci) => (
          <div key={`cat-${ci}`}
            onClick={() => { if (selectedWord !== null && !checked) placeWord(selectedWord, ci); }}
            className={`p-3 rounded-xl border-2 min-h-[90px] transition-all cursor-pointer ${selectedWord !== null && !checked ? 'border-solid ring-2 ring-offset-1 ring-offset-slate-900' : 'border-dashed'}`}
            style={{
              background: catColors[ci] || catColors[0],
              borderColor: selectedWord !== null && !checked ? (catBorders[ci] || catBorders[0]) : 'rgba(100,116,139,0.3)',
              ringColor: catBorders[ci] || catBorders[0],
            }}
            data-testid={`sort-category-${ci}`}>
            <p className={`text-xs font-bold text-center mb-2 ${catText[ci] || catText[0]}`}>
              {ci + 1}. {cat}
            </p>
            <div className="flex flex-wrap gap-1 justify-center">
              {exercise.items.map((item, wi) => {
                if (placements[wi] !== ci) return null;
                const isCorrect = item.cat === ci;
                return (
                  <span key={`placed-${wi}`}
                    onClick={(e) => { e.stopPropagation(); if (!checked) undoWord(wi); }}
                    className={`px-2 py-1 rounded text-[11px] cursor-pointer transition-all ${checked ? (isCorrect ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/15 text-red-400 line-through border border-red-500/30') : 'bg-white/10 text-white hover:bg-white/20'}`}>
                    {item.word} {!checked && <span className="text-[9px] opacity-50 ml-1">&times;</span>}
                  </span>
                );
              })}
            </div>
            {selectedWord !== null && !checked && (
              <p className="text-[10px] text-center mt-2 opacity-60 animate-pulse">Tap to place here</p>
            )}
          </div>
        ))}
      </div>

      {/* Unplaced words — tap to select */}
      <div className="flex flex-wrap gap-2 justify-center">
        {exercise.items.map((item, wi) => {
          if (placements[wi] !== undefined) return null;
          const isSelected = selectedWord === wi;
          return (
            <button key={`word-${wi}`}
              onClick={() => setSelectedWord(isSelected ? null : wi)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border-2 ${isSelected ? 'border-emerald-500 bg-emerald-500/15 text-emerald-400 scale-105 shadow-lg shadow-emerald-500/20' : 'border-slate-600 bg-slate-800 text-slate-200 hover:border-slate-400 hover:bg-slate-700'}`}
              data-testid={`sort-word-${wi}`}>
              {item.word}
            </button>
          );
        })}
      </div>

      {/* Instruction */}
      {!checked && Object.keys(placements).length < exercise.items.length && (
        <p className="text-[11px] text-muted-foreground text-center">
          {selectedWord !== null ? 'Now tap a category above to place the word' : 'Tap a word, then tap the correct category'}
        </p>
      )}
      {!checked && Object.keys(placements).length === exercise.items.length && (
        <Button size="sm" onClick={check} className="w-full bg-teal-600 hover:bg-teal-700">Check / Prüfen</Button>
      )}
    </div>
  );
}

// ─── SENTENCE ORDER EXERCISE ───
function SentenceOrder({ exercise, onComplete }) {
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [currentOrder, setCurrentOrder] = useState([]);

  useEffect(() => {
    if (exercise.sentences[activeIdx]) {
      setCurrentOrder([...exercise.sentences[activeIdx].words].sort(() => Math.random() - 0.5));
    }
  }, [activeIdx, exercise.sentences]);

  const selectWord = (word) => {
    const current = answers[activeIdx] || [];
    const updated = [...current, word];
    setAnswers(prev => ({ ...prev, [activeIdx]: updated }));
  };

  const removeWord = (idx) => {
    const current = answers[activeIdx] || [];
    const updated = current.filter((_, i) => i !== idx);
    setAnswers(prev => ({ ...prev, [activeIdx]: updated }));
  };

  const check = () => {
    setChecked(true);
    let correct = 0;
    exercise.sentences.forEach((s, i) => {
      const userAnswer = (answers[i] || []).join(' ');
      if (userAnswer === s.answer) correct++;
    });
    onComplete(correct, exercise.sentences.length);
  };

  const sentence = exercise.sentences[activeIdx];
  const placed = answers[activeIdx] || [];
  const remaining = currentOrder.filter(w => {
    const placedCount = placed.filter(p => p === w).length;
    const totalCount = currentOrder.filter(o => o === w).length;
    return placedCount < totalCount;
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground italic">{exercise.instruction}</p>
      <div className="flex items-center gap-2 mb-2">
        {exercise.sentences.map((_, i) => (
          <button key={`dot-${i}`} onClick={() => { if (!checked) setActiveIdx(i); }}
            className={`w-7 h-7 rounded-full text-xs font-bold transition-all ${i === activeIdx ? 'bg-teal-500 text-white' : answers[i]?.length > 0 ? 'bg-teal-500/20 text-teal-400' : 'bg-muted text-muted-foreground'}`}>
            {i + 1}
          </button>
        ))}
      </div>
      {/* Placed words */}
      <div className="min-h-[40px] p-3 rounded-lg border-2 border-dashed border-border flex flex-wrap gap-2">
        {placed.map((w, i) => (
          <button key={`p-${i}`} onClick={() => !checked && removeWord(i)}
            className={`px-3 py-1 rounded text-sm ${checked ? ((placed.join(' ') === sentence.answer) ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/10 text-red-400') : 'bg-primary/10 text-primary cursor-pointer hover:bg-red-500/10'}`}>
            {w}
          </button>
        ))}
        {placed.length === 0 && <span className="text-xs text-muted-foreground">Click words below to build the sentence</span>}
      </div>
      {checked && (
        <p className="text-xs text-emerald-400">Correct: {sentence.answer}</p>
      )}
      {/* Available words */}
      <div className="flex flex-wrap gap-2 justify-center">
        {remaining.map((w, i) => (
          <button key={`r-${i}`} onClick={() => !checked && selectWord(w)}
            className="px-3 py-1.5 rounded-md border border-border text-sm hover:border-teal-500 hover:bg-teal-500/5 transition-all cursor-pointer">
            {w}
          </button>
        ))}
      </div>
      {!checked && activeIdx === exercise.sentences.length - 1 && placed.length > 0 && (
        <Button size="sm" onClick={check} className="w-full bg-teal-600 hover:bg-teal-700">Check All / Alle prüfen</Button>
      )}
      {!checked && activeIdx < exercise.sentences.length - 1 && (
        <Button size="sm" variant="outline" onClick={() => setActiveIdx(prev => prev + 1)} className="w-full">
          Next sentence <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      )}
    </div>
  );
}

// ─── DIALOG MATCH EXERCISE ───
function DialogMatch({ exercise, onComplete }) {
  const [matches, setMatches] = useState({});
  const [selectedQ, setSelectedQ] = useState(null);
  const [checked, setChecked] = useState(false);
  const [shuffledAnswers] = useState(() => [...exercise.pairs.map(p => p.a)].sort(() => Math.random() - 0.5));

  const handleAnswerClick = (ansIdx) => {
    if (checked || selectedQ === null) return;
    setMatches(prev => ({ ...prev, [selectedQ]: ansIdx }));
    setSelectedQ(null);
  };

  const check = () => {
    setChecked(true);
    let correct = 0;
    exercise.pairs.forEach((pair, qi) => {
      const matchedAns = shuffledAnswers[matches[qi]];
      if (matchedAns === pair.a) correct++;
    });
    onComplete(correct, exercise.pairs.length);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground italic">{exercise.instruction}</p>
      <div className="grid md:grid-cols-2 gap-4">
        {/* Questions */}
        <div className="space-y-2">
          <p className="text-xs font-bold text-teal-400 mb-1">Fragen</p>
          {exercise.pairs.map((pair, qi) => (
            <button key={`q-${qi}`}
              onClick={() => !checked && setSelectedQ(qi)}
              className={`w-full text-left p-2.5 rounded-lg border text-xs transition-all ${selectedQ === qi ? 'border-teal-500 bg-teal-500/10' : matches[qi] !== undefined ? 'border-primary/30 bg-primary/5 opacity-60' : 'border-border hover:border-teal-500/50'}`}>
              {pair.q}
              {matches[qi] !== undefined && (
                <span className="block mt-1 text-[10px] text-muted-foreground">
                  → {shuffledAnswers[matches[qi]]}
                  {checked && (shuffledAnswers[matches[qi]] === pair.a
                    ? <CheckCircle2 className="inline h-3 w-3 ml-1 text-emerald-500" />
                    : <XCircle className="inline h-3 w-3 ml-1 text-red-500" />)}
                </span>
              )}
            </button>
          ))}
        </div>
        {/* Answers */}
        <div className="space-y-2">
          <p className="text-xs font-bold text-amber-400 mb-1">Antworten</p>
          {shuffledAnswers.map((ans, ai) => {
            const isUsed = Object.values(matches).includes(ai);
            return (
              <button key={`a-${ai}`}
                onClick={() => handleAnswerClick(ai)}
                disabled={isUsed || checked}
                className={`w-full text-left p-2.5 rounded-lg border text-xs transition-all ${isUsed ? 'opacity-40 border-border' : selectedQ !== null ? 'border-amber-500/50 hover:border-amber-500 hover:bg-amber-500/5 cursor-pointer' : 'border-border'}`}>
                {ans}
              </button>
            );
          })}
        </div>
      </div>
      {!checked && Object.keys(matches).length === exercise.pairs.length && (
        <Button size="sm" onClick={check} className="w-full bg-teal-600 hover:bg-teal-700">Check / Prüfen</Button>
      )}
    </div>
  );
}

// ─── MAIN EXERCISE SECTION ───
export function ExerciseSection({ lessonId, isActive, exercises }) {
  const [currentCat, setCurrentCat] = useState('worter');
  const [currentDiff, setCurrentDiff] = useState('leicht');
  const [completed, setCompleted] = useState({});
  const [totalScore, setTotalScore] = useState({ correct: 0, total: 0 });

  const bank = exercises || EXERCISES_A1;
  const lesson = bank[lessonId];
  if (!lesson || !isActive) return null;

  const exercise = lesson[currentCat]?.[currentDiff];
  const exerciseKey = `${lessonId}_${currentCat}_${currentDiff}`;
  const isCompleted = !!completed[exerciseKey];

  const categories = [
    { id: 'worter', label: 'Wörter', icon: '📝', color: 'bg-blue-600', hoverBg: 'hover:bg-blue-900/50', activeBorder: 'border-blue-500' },
    { id: 'strukturen', label: 'Strukturen', icon: '🔧', color: 'bg-amber-600', hoverBg: 'hover:bg-amber-900/50', activeBorder: 'border-amber-500' },
    { id: 'kommunikation', label: 'Kommunikation', icon: '💬', color: 'bg-purple-600', hoverBg: 'hover:bg-purple-900/50', activeBorder: 'border-purple-500' },
  ];
  const difficulties = [
    { id: 'leicht', label: 'Leicht / Easy', color: 'bg-emerald-600', dotColor: 'bg-emerald-400' },
    { id: 'mittel', label: 'Mittel / Medium', color: 'bg-amber-600', dotColor: 'bg-amber-400' },
    { id: 'schwer', label: 'Schwer / Hard', color: 'bg-rose-600', dotColor: 'bg-rose-400' },
  ];

  const handleComplete = (correct, total) => {
    setCompleted(prev => ({ ...prev, [exerciseKey]: { correct, total } }));
    setTotalScore(prev => ({ correct: prev.correct + correct, total: prev.total + total }));

    // Submit to backend for XP
    const pct = Math.round((correct / total) * 100);
    axios.post(`${API}/quiz/submit`, {
      quiz_id: `exercise_${exerciseKey}`,
      course_id: 'menschen_a1',
      correct, total, percentage: pct
    }).then(res => {
      if (res.data.xp_earned) toast.success(`+${res.data.xp_earned} XP`);
      trackEvent('exercise_completed', { lesson: lessonId, exercise: exerciseKey, score: pct });
    }).catch(() => {});
  };

  const completedCount = Object.keys(completed).length;
  const totalExercises = 9; // 3 cats × 3 diffs

  const renderExercise = () => {
    if (!exercise) return <p className="text-sm text-muted-foreground">Exercise not available.</p>;
    if (isCompleted) {
      const result = completed[exerciseKey];
      const pct = Math.round((result.correct / result.total) * 100);
      return (
        <div className="text-center py-6 space-y-3">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
          <p className="text-2xl font-bold text-emerald-500">{pct}%</p>
          <p className="text-sm text-muted-foreground">{result.correct}/{result.total} correct</p>
          <Button size="sm" variant="outline" onClick={() => setCompleted(prev => { const n = {...prev}; delete n[exerciseKey]; return n; })}>
            Retry
          </Button>
        </div>
      );
    }
    switch (exercise.type) {
      case 'gap': return <GapFill exercise={exercise} onComplete={handleComplete} />;
      case 'sort': return <WordSort exercise={exercise} onComplete={handleComplete} />;
      case 'order': return <SentenceOrder exercise={exercise} onComplete={handleComplete} />;
      case 'match': return <DialogMatch exercise={exercise} onComplete={handleComplete} />;
      default: return <p>Unknown exercise type</p>;
    }
  };

  return (
    <div data-testid={`exercises-${lessonId}`}>
      <h3 className="text-lg font-bold mb-1">{lesson.title}</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Übungen / Exercises — {completedCount}/{totalExercises} completed
      </p>
      <Progress value={(completedCount / totalExercises) * 100} className="h-1.5 mb-4" />

      {/* Category tabs */}
      <div className="flex gap-2 mb-3">
        {categories.map(cat => (
          <button key={cat.id} onClick={() => setCurrentCat(cat.id)}
            className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-semibold transition-all border-2 ${currentCat === cat.id ? `${cat.color} text-white border-transparent shadow-lg` : `bg-muted/30 text-muted-foreground ${cat.hoverBg} border-transparent hover:border-slate-600`}`}
            data-testid={`cat-tab-${cat.id}`}>
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Difficulty tabs */}
      <div className="flex gap-2 mb-4">
        {difficulties.map(diff => {
          const key = `${lessonId}_${currentCat}_${diff.id}`;
          const done = !!completed[key];
          return (
            <button key={diff.id} onClick={() => setCurrentDiff(diff.id)}
              className={`flex-1 py-2 px-2 rounded-lg text-[11px] font-semibold transition-all relative flex items-center justify-center gap-1.5 ${currentDiff === diff.id ? `${diff.color} text-white shadow-md` : 'bg-muted/30 text-muted-foreground hover:bg-muted border border-border'}`}
              data-testid={`diff-tab-${diff.id}`}>
              <span className={`w-2 h-2 rounded-full ${currentDiff === diff.id ? 'bg-white/50' : diff.dotColor}`} />
              {diff.label}
              {done && <CheckCircle2 className="h-3 w-3 absolute -top-1 -right-1 text-emerald-400" />}
            </button>
          );
        })}
      </div>

      {/* Exercise content */}
      <Card>
        <CardContent className="pt-4">
          {renderExercise()}
        </CardContent>
      </Card>
    </div>
  );
}
